import AddIcon from "@mui/icons-material/Add";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import TuneIcon from "@mui/icons-material/Tune";
import WorkIcon from "@mui/icons-material/Work";
import { Box, Button, Fab } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { useChosenDepartment } from "../../contexts/ChosenDepartment";
import { FEATURE_FLAGS } from "../../contexts/FeatureFlagsProvider";
import { useDepartmentFlags } from "../../hooks/useDepartmentFlags";
import { useFeatureFlag } from "../../hooks/useOpenFeatureFlag";
import { INK_BLACK, NEUTRAL, SURFACE } from "../../theme/tokens";
import { trackEvent } from "../../utils/analytics";
import { AskForReplacementDialog } from "../AskForReplacementDialog/AskForReplacementDialog";
import { RequestTimeOff } from "../RequestTimeOff/RequestTimeOff";
import { ThemedSvg } from "../UIToolkit/ThemedSvg";
export const ActionFab = ({ bottomOffset = 0 }: { bottomOffset?: number }) => {
	const { t } = useTranslation();
	const { track } = useAnalytics();
	const { chosenDepartment } = useChosenDepartment();
	const { isSnfDemo, isAlfDemo, isHospitalDemo, isMedicalDepartment } = useDepartmentFlags();
	const [isOpen, setIsOpen] = useState(false);
	const [isDialogOpen, setIsDialogOpen] = useState(false);
	const [isRequestTimeOffOpen, setIsRequestTimeOffOpen] = useState(false);
	const navigate = useNavigate();
	const isTimeOffRequestsEnabled = useFeatureFlag(FEATURE_FLAGS.TimeOffRequestsEnabled);
	const isReplacementRequestEnabled = useFeatureFlag(FEATURE_FLAGS.ReplacementRequestEnabled);

	const handleToggle = () => {
		setIsOpen(!isOpen);
		trackEvent("action_fab_toggled", {
			is_open: !isOpen,
		});
	};

	const handleAction = (callback: () => void, actionName: string) => {
		trackEvent("action_fab_option_clicked", {
			option: actionName,
		});
		callback();
		setIsOpen(false);
	};

	const handleAskForReplacement = () => {
		setIsDialogOpen(true);
		setIsOpen(false);
		trackEvent("ask_for_replacement_dialog_opened");
	};

	const handleRequestTimeOffOpened = () => {
		setIsRequestTimeOffOpen(true);
		track("request_time_off_opened");
	};

	const handleRequestTimeOffClosed = () => {
		setIsRequestTimeOffOpen(false);
		track("request_time_off_closed");
	};

	const menuItems = [
		...(!isMedicalDepartment
			? [
					{
						icon: <WorkIcon />,
						text: t("open.shifts"),
						onClick: () => navigate("/open-shifts"),
					},
					{
						icon: <TuneIcon />,
						text: t("work.preferences"),
						onClick: () => {
							const path = `/${chosenDepartment.department_id}/preferences`;
							navigate(path);
						},
					},
					...(isReplacementRequestEnabled
						? [
								{
									icon: <SwapHorizIcon />,
									text: t("ask.for.replacement"),
									onClick: handleAskForReplacement,
								},
							]
						: []),
				]
			: []),
		...(isTimeOffRequestsEnabled
			? [
					{
						icon: <ThemedSvg src="/day-off.svg" alt="Day off" size={17} color="var(--inhouse-primary)" />,
						text: t("request.time.off"),
						onClick: handleRequestTimeOffOpened,
					},
				]
			: []),
		...(isSnfDemo || isAlfDemo || isHospitalDemo
			? [
					{
						icon: <AutoAwesomeIcon />,
						text: "Ask Annie",
						onClick: () => navigate("/annie"),
					},
				]
			: []),
	];

	return (
		<>
			<Box
				sx={{
					position: "fixed",
					bottom: 16 + bottomOffset,
					right: 16,
					display: "flex",
					flexDirection: "column",
					alignItems: "flex-end",
					gap: 2,
				}}
			>
				<Box
					sx={{
						position: "absolute",
						bottom: "100%",
						right: 0,
						display: "flex",
						flexDirection: "column",
						gap: 2,
						visibility: isOpen ? "visible" : "hidden",
						opacity: isOpen ? 1 : 0,
						transition: "visibility 0s, opacity 0.2s ease-out",
						transitionDelay: isOpen ? "0s" : "0.8s",
						pointerEvents: isOpen ? "auto" : "none",
						marginBottom: 2,
					}}
				>
					{menuItems.map((item, index) => (
						<Button
							key={item.text}
							variant="contained"
							onClick={() => handleAction(item.onClick, item.text)}
							endIcon={item.icon}
							sx={{
								borderRadius: 50,
								bgcolor: SURFACE,
								color: NEUTRAL[900],
								boxShadow: `0px 4px 10px ${alpha(INK_BLACK, 0.1)}`,
								textTransform: "none",
								px: 3,
								py: 1.5,
								minWidth: "auto",
								width: "auto",
								alignSelf: "flex-end",
								whiteSpace: "nowrap",
								"&:hover": {
									bgcolor: NEUTRAL[100],
								},
								transition: "all 0.2s ease-out",
								opacity: isOpen ? 1 : 0,
								transform: isOpen ? "translateY(0)" : "translateY(20px)",
								transitionDelay: isOpen ? `${index * 0.1}s` : `${(menuItems.length - 1 - index) * 0.1}s`,
							}}
						>
							{item.text}
						</Button>
					))}
				</Box>
				<Fab
					data-testid="action-fab-toggle"
					color="primary"
					aria-label="add"
					onClick={handleToggle}
					sx={(theme) => ({
						transition: "all 0.3s ease",
						color: theme.palette.primary.contrastText,
						"& .MuiSvgIcon-root": {
							color: theme.palette.primary.contrastText,
							fill: theme.palette.primary.contrastText,
							transition: "transform 0.3s ease",
							transform: isOpen ? "rotate(45deg)" : "rotate(180deg)",
						},
					})}
				>
					<AddIcon />
				</Fab>
			</Box>
			<AskForReplacementDialog
				open={isDialogOpen}
				onClose={() => {
					setIsDialogOpen(false);
					trackEvent("ask_for_replacement_dialog_closed");
				}}
			/>
			<RequestTimeOff open={isRequestTimeOffOpen} onClose={handleRequestTimeOffClosed} />
		</>
	);
};
